import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Car, MapPin, Phone, Star, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: 'Looking for driver',
  accepted: 'Driver assigned',
  arriving: 'Driver is on the way',
  in_progress: 'On the trip',
  completed: 'Trip completed',
  cancelled: 'Trip cancelled',
};

const SharedRideTrack = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchShared = async () => {
    try {
      const res = await axios.get(`${API}/api/public/shared-ride/${token}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to load shared ride');
    }
  };

  useEffect(() => {
    fetchShared();
    intervalRef.current = setInterval(fetchShared, 8000);
    return () => clearInterval(intervalRef.current);
  }, [token]); // eslint-disable-line

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="shared-ride-error">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">🔗</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Link Unavailable</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  const isFinal = ['completed', 'cancelled'].includes(data.status);
  const mapUrl = data.driverLocation?.latitude
    ? `https://maps.google.com/maps?q=${data.driverLocation.latitude},${data.driverLocation.longitude}&z=15&output=embed`
    : (data.pickupLocation?.latitude
        ? `https://maps.google.com/maps?q=${data.pickupLocation.latitude},${data.pickupLocation.longitude}&z=14&output=embed`
        : null);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="shared-ride-page">
      <header className="bg-white border-b px-4 py-3 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <span className="text-pink-600 font-bold text-lg">Tekstar</span>
          <span className="text-xs text-gray-500">Live trip share</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Following</span>
            <span className="text-xs px-2 py-1 rounded-full bg-pink-50 text-pink-700 font-medium" data-testid="shared-ride-status">
              {STATUS_LABELS[data.status] || data.status}
            </span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{data.riderName}'s ride</h1>
        </div>

        {mapUrl && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border">
            <iframe
              src={mapUrl}
              title="Live ride map"
              className="w-full h-72 border-0"
              loading="lazy"
              data-testid="shared-ride-map"
            />
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-4 border space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Pickup</p>
              <p className="text-sm text-gray-900 font-medium">{data.pickup}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="h-4 w-4 text-pink-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Drop-off</p>
              <p className="text-sm text-gray-900 font-medium">{data.dropoff}</p>
            </div>
          </div>
        </div>

        {data.driver && (
          <div className="bg-white rounded-xl shadow-sm p-4 border">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Car className="h-4 w-4 text-pink-600" /> Driver
            </h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-base font-semibold text-gray-900">{data.driver.name || 'Driver'}</p>
                <p className="text-sm text-gray-600">
                  {data.driver.vehicle} {data.driver.vehicleColor && `· ${data.driver.vehicleColor}`}
                </p>
                {data.driver.plateNumber && (
                  <p className="text-xs text-gray-500 mt-1">Plate: {data.driver.plateNumber}</p>
                )}
              </div>
              <div className="flex items-center gap-1 text-sm">
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                <span className="font-medium">{(data.driver.rating || 5).toFixed(1)}</span>
              </div>
            </div>
          </div>
        )}

        {!isFinal && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-blue-600" />
            <span className="text-xs text-blue-800">Live updates every 8 seconds. Link expires {new Date(data.expiresAt).toLocaleString()}.</span>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 pt-4">Powered by Tekstar · Zambia</p>
      </div>
    </div>
  );
};

export default SharedRideTrack;
